import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty } from 'lodash';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import ratios from 'Components/utils/ratios/ratios.utils';
import type { BadgeProps } from 'Components/frameworks/badge/Badge';
import { Badge } from 'Components/frameworks/badge/Badge';
import { Hero } from 'Components/widgets/hero/Hero';
import { Multimedia } from 'Components/widgets/multimedia/Multimedia';
import type { MultimediaProps } from 'Components/widgets/multimedia/Multimedia';
import type { ImageSlideShowProps } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import { ImageSlideShow } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import { themes } from './Sheet.themes';
type Props = {
  children?: ReactNode;
  header?: ReactNode;
  className?: string;
  diamond?: BadgeProps;
  footer?: ReactNode;
  fullWidthContent?: boolean;
  immersive?: { url?: string; className?: string } | null;
  isExclusiveCollection?: boolean;
  multimedia?: unknown;
  nextLabel?: string;
  previousLabel?: string;
  slideshow?: Partial<ImageSlideShowProps>;
  theme?: ThemeKey<typeof themes>;
};
export function Sheet({
  children,
  header,
  className,
  diamond,
  immersive,
  isExclusiveCollection,
  footer,
  fullWidthContent,
  multimedia,
  slideshow = {},
  theme = 'default',
}: Props) {
  const { isTablet } = useResponsive();
  const [imageIndex, setImageIndex] = useState(0);

  if (!Children.count(children)) {
    return null;
  }

  const hasSlideshow = !isEmpty(slideshow.media);
  const hasDiamond = !isEmpty(diamond);
  const {
    thBody,
    thContent,
    thDiamond,
    thDiamondClassName,
    thFooter,
    thHeader,
    thHeaderContainer,
    thHero,
    thHeroImmersive,
    thMultimediaOverlay,
    thRoot,
    thSlideshow,
    thTop,
  } = themes[theme]({
    hasDiamond,
    hasSlideshow,
    isExclusiveCollection,
    fullWidthContent,
  }) as DefaultThemeStyles<typeof themes>;
  return (
    <section className={classnames(thRoot, className)}>
      {header && (
        <div className={thHeaderContainer} data-testid="Sheet-header">
          <div className={thHeader}>{header}</div>
        </div>
      )}
      {(!isEmpty(immersive) || hasSlideshow) && (
        <div className={thTop}>
          {!isEmpty(immersive) && !hasSlideshow && (
            <div className={thHero}>
              <Hero
                {...immersive}
                className={classnames(thHeroImmersive, immersive.className)}
                size="medium"
              />
            </div>
          )}
          {hasSlideshow && (
            <React.Fragment>
              <div className={thSlideshow}>
                <ImageSlideShow
                  media={slideshow.media}
                  nbItemsToShow={1}
                  centerPadding={isTablet ? 0.25 : 0}
                  fadeUnseenItems={isTablet}
                  spacerWidth={isTablet ? 5 : 0}
                  ratio={ratios.XL}
                  onChangeIndex={setImageIndex}
                  {...slideshow}
                />
                <div className="absolute start-0 top-0 mx-10 my-10 rounded-4 bg-darkGrey/80 px-15 py-10 text-14 text-white shadow-sm md:bottom-0 md:top-auto md:mx-20">
                  {`${imageIndex + 1}/${slideshow.media?.length ?? 0}`}
                </div>
              </div>
              {!isEmpty(multimedia) && (
                <div className={thMultimediaOverlay}>
                  <Multimedia
                    {...({
                      ...(typeof multimedia === 'object' && multimedia !== null ? multimedia : {}),
                      className: 'flex h-full flex-col',
                      hasFullScreenButton: true,
                      initialIndex: imageIndex,
                    } as MultimediaProps)}
                  />
                </div>
              )}
            </React.Fragment>
          )}
          {hasDiamond && (
            <div className={thDiamond}>
              <Badge
                {...diamond}
                className={classnames(thDiamondClassName, diamond?.className)}
                variant="diamond"
              />
            </div>
          )}
        </div>
      )}
      <div className={thBody} data-testid="Sheet-body">
        <div className={thContent}>{children}</div>
      </div>
      {!!footer && (
        <footer className={thFooter}>
          <div className={thContent}>{footer}</div>
        </footer>
      )}
    </section>
  );
}
